import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import {
  Type, Image as ImageIcon, ArrowRight, Minus, Hash, Calendar, CheckSquare, Sliders, ChevronRight
} from 'lucide-react';

const sectionMotion = {
  initial: { height: 0, opacity: 0 },
  animate: { height: 'auto', opacity: 1 },
  exit: { height: 0, opacity: 0 },
  transition: { duration: 0.2, ease: [0.4, 0, 0.2, 1] },
};

const colorClasses = {
  primary: 'hover:bg-primary-50 hover:text-primary-900 hover:border-primary-400',
  blue: 'hover:bg-blue-50 hover:text-blue-900 hover:border-blue-400',
  purple: 'hover:bg-purple-50 hover:text-purple-900 hover:border-purple-400',
  gray: 'hover:bg-gray-50 hover:text-gray-900 hover:border-gray-400',
};

// Transparent 1×1 PNG used to suppress the browser's native HTML5 drag ghost.
// Without this, the browser renders a faded snapshot of the entire sidebar
// row, which duplicates (and overlaps) our in-canvas drop preview.
const EMPTY_DRAG_IMAGE = typeof Image !== 'undefined' ? (() => {
  const img = new Image();
  img.src = 'data:image/gif;base64,R0lGODlhAQABAIAAAAAAAP///yH5BAEAAAAALAAAAAABAAEAAAIBRAA7';
  return img;
})() : null;

const ElementsPanel = ({
  setDraggingFromSidebar,
  setDragPreviewPosition,
}) => {
  const [expandedSections, setExpandedSections] = useState({
    basic: false,
    input: false,
  });

  const handleDragStart = (e, type) => {
    e.dataTransfer.effectAllowed = 'copy';
    e.dataTransfer.setData('application/json', JSON.stringify({ type }));
    if (EMPTY_DRAG_IMAGE) {
      e.dataTransfer.setDragImage(EMPTY_DRAG_IMAGE, 0, 0);
    }
    setDraggingFromSidebar({ type });
  };

  const handleDragEnd = () => {
    setDraggingFromSidebar(null);
    setDragPreviewPosition(null);
  };

  const toggleSection = (section) => {
    setExpandedSections((prev) => ({
      ...prev,
      [section]: !prev[section],
    }));
  };

  return (
    <>
      {/* Basic Elements */}
      <div>
        <button
          type="button"
          onClick={() => toggleSection('basic')}
          className="mb-3 flex w-full items-center justify-between rounded-lg px-1 py-1.5 text-left transition-colors hover:bg-gray-100/80 dark:hover:bg-gray-700/80"
          aria-expanded={expandedSections.basic}
          aria-controls="elements-panel-basic"
        >
          <div className="flex items-center space-x-2">
            <div className="p-1.5 bg-primary-50 text-primary-600 rounded-lg">
              <Type className="w-3.5 h-3.5" />
            </div>
            <div>
              <h3 className="text-xs font-semibold text-gray-700 uppercase tracking-wide">Basic Elements</h3>
              <p className="text-[10px] text-gray-500">4 elements</p>
            </div>
          </div>
          <motion.span
            animate={{ rotate: expandedSections.basic ? 90 : 0 }}
            transition={{ duration: 0.2, ease: [0.4, 0, 0.2, 1] }}
            className="inline-flex"
          >
            <ChevronRight
              className={`h-4 w-4 ${expandedSections.basic ? 'text-primary-600' : 'text-gray-400'}`}
            />
          </motion.span>
        </button>
        <AnimatePresence initial={false}>
          {expandedSections.basic && (
            <motion.div
              id="elements-panel-basic"
              key="basic"
              {...sectionMotion}
              className="overflow-hidden"
            >
              <div className="space-y-2 pb-0.5">
                <div
                  draggable
                  onDragStart={(e) => handleDragStart(e, 'text')}
                  onDragEnd={handleDragEnd}
                  className={`w-full flex items-center px-3 py-2.5 text-sm text-gray-700 bg-white dark:bg-gray-800 rounded border border-gray-200 dark:border-gray-700 transition-colors cursor-grab active:cursor-grabbing select-none ${colorClasses.primary}`}
                >
                  <Type className="w-4 h-4 mr-2.5 flex-shrink-0" />
                  <span className="font-medium">Text</span>
                </div>
                <div
                  draggable
                  onDragStart={(e) => handleDragStart(e, 'image')}
                  onDragEnd={handleDragEnd}
                  className={`w-full flex items-center px-3 py-2.5 text-sm text-gray-700 bg-white dark:bg-gray-800 rounded border border-gray-200 dark:border-gray-700 transition-colors cursor-grab active:cursor-grabbing select-none ${colorClasses.primary}`}
                >
                  <ImageIcon className="w-4 h-4 mr-2.5 flex-shrink-0" />
                  <span className="font-medium">Image</span>
                </div>
                <div
                  draggable
                  onDragStart={(e) => handleDragStart(e, 'button')}
                  onDragEnd={handleDragEnd}
                  className={`w-full flex items-center px-3 py-2.5 text-sm text-gray-700 bg-white dark:bg-gray-800 rounded border border-gray-200 dark:border-gray-700 transition-colors cursor-grab active:cursor-grabbing select-none ${colorClasses.blue}`}
                >
                  <ArrowRight className="w-4 h-4 mr-2.5 flex-shrink-0" />
                  <span className="font-medium">Button (Next/Back)</span>
                </div>
                <div
                  draggable
                  onDragStart={(e) => handleDragStart(e, 'divider')}
                  onDragEnd={handleDragEnd}
                  className={`w-full flex items-center px-3 py-2.5 text-sm text-gray-700 bg-white dark:bg-gray-800 rounded border border-gray-200 dark:border-gray-700 transition-colors cursor-grab active:cursor-grabbing select-none ${colorClasses.gray}`}
                >
                  <Minus className="w-4 h-4 mr-2.5 flex-shrink-0" />
                  <span className="font-medium">Divider</span>
                </div>
              </div>
            </motion.div>
          )}
        </AnimatePresence>
      </div>

      {/* Input Elements */}
      <div>
        <button
          type="button"
          onClick={() => toggleSection('input')}
          className="mb-3 flex w-full items-center justify-between rounded-lg px-1 py-1.5 text-left transition-colors hover:bg-gray-100/80 dark:hover:bg-gray-700/80"
          aria-expanded={expandedSections.input}
          aria-controls="elements-panel-input"
        >
          <div className="flex items-center space-x-2">
            <div className="p-1.5 bg-purple-50 text-purple-600 rounded-lg">
              <Hash className="w-3.5 h-3.5" />
            </div>
            <div>
              <h3 className="text-xs font-semibold text-gray-700 uppercase tracking-wide">Input Fields</h3>
              <p className="text-[10px] text-gray-500">5 elements</p>
            </div>
          </div>
          <motion.span
            animate={{ rotate: expandedSections.input ? 90 : 0 }}
            transition={{ duration: 0.2, ease: [0.4, 0, 0.2, 1] }}
            className="inline-flex"
          >
            <ChevronRight
              className={`h-4 w-4 ${expandedSections.input ? 'text-purple-600' : 'text-gray-400'}`}
            />
          </motion.span>
        </button>
        <AnimatePresence initial={false}>
          {expandedSections.input && (
            <motion.div
              id="elements-panel-input"
              key="input"
              {...sectionMotion}
              className="overflow-hidden"
            >
              <div className="space-y-2 pb-0.5">
                <div
                  draggable
                  onDragStart={(e) => handleDragStart(e, 'text-input')}
                  onDragEnd={handleDragEnd}
                  className={`w-full flex items-center px-3 py-2.5 text-sm text-gray-700 bg-white dark:bg-gray-800 rounded border border-gray-200 dark:border-gray-700 transition-colors cursor-grab active:cursor-grabbing select-none ${colorClasses.purple}`}
                >
                  <Type className="w-4 h-4 mr-2.5 flex-shrink-0" />
                  <span className="font-medium">Text Input</span>
                </div>
                <div
                  draggable
                  onDragStart={(e) => handleDragStart(e, 'number-input')}
                  onDragEnd={handleDragEnd}
                  className={`w-full flex items-center px-3 py-2.5 text-sm text-gray-700 bg-white dark:bg-gray-800 rounded border border-gray-200 dark:border-gray-700 transition-colors cursor-grab active:cursor-grabbing select-none ${colorClasses.purple}`}
                >
                  <Hash className="w-4 h-4 mr-2.5 flex-shrink-0" />
                  <span className="font-medium">Number Input</span>
                </div>
                <div
                  draggable
                  onDragStart={(e) => handleDragStart(e, 'date-picker')}
                  onDragEnd={handleDragEnd}
                  className={`w-full flex items-center px-3 py-2.5 text-sm text-gray-700 bg-white dark:bg-gray-800 rounded border border-gray-200 dark:border-gray-700 transition-colors cursor-grab active:cursor-grabbing select-none ${colorClasses.purple}`}
                >
                  <Calendar className="w-4 h-4 mr-2.5 flex-shrink-0" />
                  <span className="font-medium">Date Picker</span>
                </div>
                <div
                  draggable
                  onDragStart={(e) => handleDragStart(e, 'checkbox')}
                  onDragEnd={handleDragEnd}
                  className={`w-full flex items-center px-3 py-2.5 text-sm text-gray-700 bg-white dark:bg-gray-800 rounded border border-gray-200 dark:border-gray-700 transition-colors cursor-grab active:cursor-grabbing select-none ${colorClasses.purple}`}
                >
                  <CheckSquare className="w-4 h-4 mr-2.5 flex-shrink-0" />
                  <span className="font-medium">Checkbox Group</span>
                </div>
                <div
                  draggable
                  onDragStart={(e) => handleDragStart(e, 'scale')}
                  onDragEnd={handleDragEnd}
                  className={`w-full flex items-center px-3 py-2.5 text-sm text-gray-700 bg-white dark:bg-gray-800 rounded border border-gray-200 dark:border-gray-700 transition-colors cursor-grab active:cursor-grabbing select-none ${colorClasses.purple}`}
                >
                  <Sliders className="w-4 h-4 mr-2.5 flex-shrink-0" />
                  <span className="font-medium">Scale / Slider</span>
                </div>
              </div>
            </motion.div>
          )}
        </AnimatePresence>
      </div>
    </>
  );
};

export default ElementsPanel;
